import React, { useEffect, useState, useRef } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { CheckIcon, ArrowRightIcon, ChevronRightIcon, BuildingIcon, UsersIcon, BarChartIcon, UserIcon, DatabaseIcon, GlobeIcon, BookOpenIcon, AwardIcon, ClockIcon, MailIcon, LockIcon, CreditCardIcon, RefreshCwIcon, ClipboardCheckIcon, BadgeCheckIcon, HeartIcon, ShieldIcon, ServerIcon, SmartphoneIcon, BriefcaseIcon } from 'lucide-react';
export const MinimalHomeIreland = () => {
  return <div className="flex flex-col min-h-screen w-full bg-white text-charcoal" data-id="element-3894">
      <Header data-id="element-3895" />
      <main className="flex-grow" data-id="element-3896">
        <HeroSection data-id="element-3897" />
        <TrustBadgesSection data-id="element-3898" />
        <ValuePropositionSection data-id="element-3899" />
        <BenefitsSection data-id="element-3900" />
        <TeamsSection data-id="element-3901" />
        <MembershipSection data-id="element-3902" />
        <EnterpriseSection data-id="element-3903" />
        <TrainingCategoriesSection data-id="element-3904" />
        <ClosingCTASection data-id="element-3905" />
      </main>
      <Footer data-id="element-3906" />
    </div>;
};
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    setIsVisible(true);
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden" data-id="element-3907">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0" data-id="element-3908">
        <img src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80" alt="Professional training environment" className="w-full h-full object-cover" data-id="element-3909" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/85 to-charcoal/75" data-id="element-3910"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-soft-light" data-id="element-3911"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-3912">
        <div className="max-w-3xl mx-auto text-center" data-id="element-3913">
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-3914">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-teal/20 backdrop-blur-sm border border-teal/30 mb-6" data-id="element-3915">
              <GlobeIcon className="h-4 w-4 text-aqua mr-2" data-id="element-3916" />
              <span className="text-white/90 text-sm font-medium" data-id="element-3917">
                Training for Ireland, the UK & Europe
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight" data-id="element-3918">
              Train. Upskill. <br data-id="element-3919" />
              <span className="text-gradient bg-gradient-to-r from-teal to-aqua bg-clip-text text-transparent" data-id="element-3920">
                Stay Compliant.
              </span>
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed" data-id="element-3921">
              Accredited compliance training, workplace skills, and business
              courses — built to meet legislation and trusted by businesses of
              every size.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center" data-id="element-3922">
              <a href="/training" className="px-8 py-4 bg-teal text-white font-medium rounded-lg hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 shadow-lg" data-id="element-3923">
                Start Training
              </a>
              <a href="/demo" className="px-8 py-4 bg-transparent text-white font-medium rounded-lg border-2 border-white hover:bg-white/10 transition-all duration-300" data-id="element-3924">
                Book a Demo
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const TrustBadgesSection = () => {
  return <section className="py-10 border-b border-gray-200 bg-white" data-id="element-3925">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3926">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16" data-id="element-3927">
          <div className="flex flex-col items-center" data-id="element-3928">
            <span className="text-charcoal/60 text-sm font-medium mb-3" data-id="element-3929">
              CPD Accredited
            </span>
            <div className="h-10 w-24 bg-gray-100 rounded" data-id="element-3930"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-3931">
            <span className="text-charcoal/60 text-sm font-medium mb-3" data-id="element-3932">
              ROSPA Member
            </span>
            <div className="h-10 w-24 bg-gray-100 rounded" data-id="element-3933"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-3934">
            <span className="text-charcoal/60 text-sm font-medium mb-3" data-id="element-3935">
              Irish Trade Federation
            </span>
            <div className="h-10 w-24 bg-gray-100 rounded" data-id="element-3936"></div>
          </div>
          <div className="flex flex-col items-center" data-id="element-3937">
            <div className="flex" data-id="element-3938">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-teal" fill="currentColor" data-id="element-3939" />)}
            </div>
            <span className="mt-2 text-charcoal/60 text-sm font-medium" data-id="element-3940">
              97% Completion Rate in 2024
            </span>
          </div>
        </div>
      </div>
    </section>;
};
const ValuePropositionSection = () => {
  return <section className="py-20 bg-white" data-id="element-3941">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3942">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3943">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-3944">
            One academy. Every advantage.
          </h2>
          <p className="text-lg text-charcoal/70 max-w-2xl mx-auto" data-id="element-3945">
            Kivarro isn't just courses — it's a training ecosystem. Whether
            you're an individual, a manager, or an enterprise, every learner
            gets the same premium experience.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3946">
          <div className="bg-gray-50 rounded-lg p-8 transition-all duration-300 hover:shadow-md" data-id="element-3947">
            <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-3948">
              <UserIcon className="w-7 h-7 text-teal" data-id="element-3949" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-charcoal" data-id="element-3950">
              For Individuals
            </h3>
            <p className="text-charcoal/70" data-id="element-3951">
              Access professional training anytime, anywhere. Get certified
              instantly and boost your career prospects.
            </p>
          </div>
          <div className="bg-teal text-white rounded-lg p-8 transition-all duration-300 hover:shadow-md" data-id="element-3952">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6" data-id="element-3953">
              <UsersIcon className="w-7 h-7 text-white" data-id="element-3954" />
            </div>
            <h3 className="text-xl font-bold mb-3" data-id="element-3955">For Teams</h3>
            <p className="text-white/90" data-id="element-3956">
              Assign, track, and manage training for your entire team with
              built-in dashboards and reporting tools.
            </p>
          </div>
          <div className="bg-charcoal text-white rounded-lg p-8 transition-all duration-300 hover:shadow-md" data-id="element-3957">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6" data-id="element-3958">
              <BuildingIcon className="w-7 h-7 text-white" data-id="element-3959" />
            </div>
            <h3 className="text-xl font-bold mb-3" data-id="element-3960">For Enterprises</h3>
            <p className="text-white/90" data-id="element-3961">
              Scale training across your organization with your own branded
              academy, SSO, and advanced analytics.
            </p>
          </div>
        </div>
      </div>
    </section>;
};
const BenefitsSection = () => {
  const benefits = [{
    icon: <ClockIcon className="w-6 h-6 text-teal" data-id="element-3962" />,
    title: 'Start Instantly',
    description: 'Sign up and begin training right away — no delays, no waiting periods.'
  }, {
    icon: <SmartphoneIcon className="w-6 h-6 text-teal" data-id="element-3963" />,
    title: 'Train Anywhere',
    description: '24/7 access across desktop, mobile, and tablet — learn on your schedule.'
  }, {
    icon: <BadgeCheckIcon className="w-6 h-6 text-teal" data-id="element-3964" />,
    title: 'Instant Certification',
    description: 'Certificates issued the moment you complete a course.'
  }, {
    icon: <AwardIcon className="w-6 h-6 text-teal" data-id="element-3965" />,
    title: 'Certificate Guarantee',
    description: 'Always accepted by auditors and regulators.'
  }, {
    icon: <LockIcon className="w-6 h-6 text-teal" data-id="element-3966" />,
    title: 'Stored Forever',
    description: 'All certificates and compliance records kept in one secure hub.'
  }, {
    icon: <RefreshCwIcon className="w-6 h-6 text-teal" data-id="element-3967" />,
    title: 'Unlimited Retries',
    description: 'Train and retest until you pass — no extra fees.'
  }];
  return <section className="py-20 bg-gray-50" data-id="element-3968">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3969">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3970">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-3971">
            Everything you expect. And more.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-3972">
          {benefits.map((benefit, index) => <div key={index} className="bg-white rounded-lg p-6 shadow-sm flex items-start" data-id="element-3973">
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-3974">
                {benefit.icon}
              </div>
              <div data-id="element-3975">
                <h3 className="text-lg font-bold mb-2 text-charcoal" data-id="element-3976">
                  {benefit.title}
                </h3>
                <p className="text-charcoal/70 text-sm" data-id="element-3977">
                  {benefit.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const TeamsSection = () => {
  return <section className="py-20 bg-white" data-id="element-3978">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3979">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto" data-id="element-3980">
          <div data-id="element-3981">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-charcoal tracking-tight" data-id="element-3982">
              Training that scales effortlessly.
            </h2>
            <div className="space-y-6 mb-10" data-id="element-3983">
              <div className="flex" data-id="element-3984">
                <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-3985">
                  <MailIcon className="w-6 h-6 text-teal" data-id="element-3986" />
                </div>
                <div data-id="element-3987">
                  <h3 className="text-lg font-bold mb-2 text-charcoal" data-id="element-3988">
                    Roll out training instantly
                  </h3>
                  <p className="text-charcoal/70" data-id="element-3989">
                    Automated login emails are sent as soon as staff are added.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-3990">
                <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-3991">
                  <CreditCardIcon className="w-6 h-6 text-teal" data-id="element-3992" />
                </div>
                <div data-id="element-3993">
                  <h3 className="text-lg font-bold mb-2 text-charcoal" data-id="element-3994">
                    Add seats anytime
                  </h3>
                  <p className="text-charcoal/70" data-id="element-3995">
                    Scale your training without needing to contact support.
                  </p>
                </div>
              </div>
              <div className="flex" data-id="element-3996">
                <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mr-5 flex-shrink-0" data-id="element-3997">
                  <BarChartIcon className="w-6 h-6 text-teal" data-id="element-3998" />
                </div>
                <div data-id="element-3999">
                  <h3 className="text-lg font-bold mb-2 text-charcoal" data-id="element-4000">
                    Track progress and export reports
                  </h3>
                  <p className="text-charcoal/70" data-id="element-4001">
                    Manage compliance with a built-in dashboard.
                  </p>
                </div>
              </div>
            </div>
            <a href="/teams" className="inline-flex items-center px-8 py-4 bg-teal text-white font-medium rounded-lg transition-all duration-300 hover:bg-teal/90 shadow-lg" data-id="element-4002">
              Train Your Team
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-4003" />
            </a>
          </div>
          <div className="relative rounded-lg overflow-hidden shadow-xl" data-id="element-4004">
            <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Team Training Dashboard" className="w-full h-auto object-cover" data-id="element-4005" />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" data-id="element-4006"></div>
            <div className="absolute bottom-8 left-8 right-8" data-id="element-4007">
              <div className="bg-white p-6 rounded-lg shadow-lg" data-id="element-4008">
                <div className="flex items-center justify-between mb-4" data-id="element-4009">
                  <h4 className="text-lg font-bold text-charcoal" data-id="element-4010">
                    Team Dashboard
                  </h4>
                  <div className="flex" data-id="element-4011">
                    <div className="w-2 h-2 rounded-full bg-teal mr-1 animate-pulse" data-id="element-4012"></div>
                    <div className="w-2 h-2 rounded-full bg-teal/60 mr-1 animate-pulse" style={{
                    animationDelay: '0.2s'
                  }} data-id="element-4013"></div>
                    <div className="w-2 h-2 rounded-full bg-teal/30 animate-pulse" style={{
                    animationDelay: '0.4s'
                  }} data-id="element-4014"></div>
                  </div>
                </div>
                <div className="flex justify-between text-sm" data-id="element-4015">
                  <div data-id="element-4016">
                    <div className="text-charcoal/60" data-id="element-4017">Assigned</div>
                    <div className="text-xl font-bold text-charcoal" data-id="element-4018">24</div>
                  </div>
                  <div data-id="element-4019">
                    <div className="text-charcoal/60" data-id="element-4020">In Progress</div>
                    <div className="text-xl font-bold text-charcoal" data-id="element-4021">18</div>
                  </div>
                  <div data-id="element-4022">
                    <div className="text-charcoal/60" data-id="element-4023">Completed</div>
                    <div className="text-xl font-bold text-charcoal" data-id="element-4024">6</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const MembershipSection = () => {
  return <section className="py-20 bg-gray-50" data-id="element-4025">
      <div className="container mx-auto px-4 md:px-6" data-id="element-4026">
        <div className="max-w-6xl mx-auto" data-id="element-4027">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-4028">
            <div data-id="element-4029">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-4030">
                Unlimited learning. <br data-id="element-4031" />
                One subscription.
              </h2>
              <p className="text-lg text-charcoal/70 mb-8" data-id="element-4032">
                For learners and businesses who never stop growing, Kivarro
                offers All-Access Membership — one subscription unlocks the
                entire training library.
              </p>
              <div className="space-y-4 mb-10" data-id="element-4033">
                <div className="flex items-start" data-id="element-4034">
                  <CheckIcon className="w-6 h-6 text-teal mt-0.5 mr-3 flex-shrink-0" data-id="element-4035" />
                  <p className="text-charcoal/80" data-id="element-4036">
                    Every course. Every update. Always included.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-4037">
                  <CheckIcon className="w-6 h-6 text-teal mt-0.5 mr-3 flex-shrink-0" data-id="element-4038" />
                  <p className="text-charcoal/80" data-id="element-4039">
                    Ideal for continuous upskilling and compliance.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-4040">
                  <CheckIcon className="w-6 h-6 text-teal mt-0.5 mr-3 flex-shrink-0" data-id="element-4041" />
                  <p className="text-charcoal/80" data-id="element-4042">
                    Simple pricing for individuals, teams, and enterprises.
                  </p>
                </div>
              </div>
              <a href="/membership" className="inline-flex items-center px-8 py-4 bg-teal text-white font-medium rounded-lg transition-all duration-300 hover:bg-teal/90 shadow-lg" data-id="element-4043">
                Explore Membership
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-4044" />
              </a>
            </div>
            <div data-id="element-4045">
              <div className="bg-white rounded-xl overflow-hidden shadow-xl" data-id="element-4046">
                <div className="bg-charcoal p-8 text-white" data-id="element-4047">
                  <div className="inline-flex items-center px-3 py-1 bg-aqua/90 text-white text-sm font-medium rounded-full mb-4" data-id="element-4048">
                    Most Popular
                  </div>
                  <h3 className="text-2xl font-bold mb-2" data-id="element-4049">
                    All-Access Membership
                  </h3>
                  <p className="text-white/80" data-id="element-4050">
                    Unlimited training for continuous learning
                  </p>
                </div>
                <div className="p-8" data-id="element-4051">
                  <div className="flex items-baseline mb-6" data-id="element-4052">
                    <span className="text-3xl font-bold text-charcoal" data-id="element-4053">
                      €39
                    </span>
                    <span className="text-charcoal/60 ml-2" data-id="element-4054">
                      / month per user
                    </span>
                  </div>
                  <div className="space-y-4 mb-8" data-id="element-4055">
                    <div className="flex items-center" data-id="element-4056">
                      <CheckIcon className="w-5 h-5 text-teal mr-3 flex-shrink-0" data-id="element-4057" />
                      <p className="text-charcoal/80" data-id="element-4058">
                        Full access to all 125+ courses
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-4059">
                      <CheckIcon className="w-5 h-5 text-teal mr-3 flex-shrink-0" data-id="element-4060" />
                      <p className="text-charcoal/80" data-id="element-4061">
                        Unlimited certifications
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-4062">
                      <CheckIcon className="w-5 h-5 text-teal mr-3 flex-shrink-0" data-id="element-4063" />
                      <p className="text-charcoal/80" data-id="element-4064">
                        New courses as they're released
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-4065">
                      <CheckIcon className="w-5 h-5 text-teal mr-3 flex-shrink-0" data-id="element-4066" />
                      <p className="text-charcoal/80" data-id="element-4067">Mobile app access</p>
                    </div>
                    <div className="flex items-center" data-id="element-4068">
                      <CheckIcon className="w-5 h-5 text-teal mr-3 flex-shrink-0" data-id="element-4069" />
                      <p className="text-charcoal/80" data-id="element-4070">
                        Team management dashboard
                      </p>
                    </div>
                  </div>
                  <a href="/membership/signup" className="block w-full py-3 bg-teal text-white font-medium rounded-lg transition-all duration-300 hover:bg-teal/90 text-center" data-id="element-4071">
                    Start Free Trial
                  </a>
                  <p className="text-center text-charcoal/60 text-sm mt-3" data-id="element-4072">
                    30-day free trial, cancel anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const EnterpriseSection = () => {
  return <section className="py-20 bg-charcoal text-white" data-id="element-4073">
      <div className="container mx-auto px-4 md:px-6" data-id="element-4074">
        <div className="max-w-6xl mx-auto" data-id="element-4075">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-4076">
            <div data-id="element-4077">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight" data-id="element-4078">
                Your academy. <br data-id="element-4079" />
                Your brand.
              </h2>
              <p className="text-white/80 text-lg mb-8" data-id="element-4080">
                Go beyond training — launch your own fully branded compliance
                academy.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-4081">
                <div className="flex items-start" data-id="element-4082">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-4083">
                    <GlobeIcon className="w-5 h-5 text-aqua" data-id="element-4084" />
                  </div>
                  <div data-id="element-4085">
                    <h3 className="font-bold text-white mb-1" data-id="element-4086">
                      Custom branding
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-4087">
                      Your logo, colors, and domain
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4088">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-4089">
                    <BarChartIcon className="w-5 h-5 text-aqua" data-id="element-4090" />
                  </div>
                  <div data-id="element-4091">
                    <h3 className="font-bold text-white mb-1" data-id="element-4092">
                      Advanced analytics
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-4093">
                      Detailed reporting and insights
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4094">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-4095">
                    <ServerIcon className="w-5 h-5 text-aqua" data-id="element-4096" />
                  </div>
                  <div data-id="element-4097">
                    <h3 className="font-bold text-white mb-1" data-id="element-4098">
                      SSO & provisioning
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-4099">
                      Seamless user management
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4100">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-4101">
                    <HeartIcon className="w-5 h-5 text-aqua" data-id="element-4102" />
                  </div>
                  <div data-id="element-4103">
                    <h3 className="font-bold text-white mb-1" data-id="element-4104">
                      Priority support
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-4105">
                      Dedicated account manager
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-4106">
                <a href="/enterprise/demo" className="px-8 py-4 bg-white text-charcoal font-medium rounded-lg transition-all duration-300 hover:bg-gray-100 text-center shadow-lg" data-id="element-4107">
                  Book a Demo
                </a>
                <a href="/enterprise" className="px-8 py-4 border-2 border-white text-white font-medium rounded-lg hover:bg-white/10 transition-all duration-300 text-center" data-id="element-4108">
                  Learn More
                </a>
              </div>
            </div>
            <div data-id="element-4109">
              <div className="relative rounded-lg overflow-hidden shadow-2xl" data-id="element-4110">
                <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Enterprise Academy Dashboard" className="w-full h-auto object-cover" data-id="element-4111" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" data-id="element-4112"></div>
                <div className="absolute top-6 left-6 right-6 flex justify-between items-center" data-id="element-4113">
                  <div className="flex items-center bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg" data-id="element-4114">
                    <div className="w-6 h-6 bg-teal rounded-md mr-3" data-id="element-4115"></div>
                    <span className="font-medium text-charcoal" data-id="element-4116">
                      Your Brand
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const TrainingCategoriesSection = () => {
  const categories = [{
    icon: <ShieldIcon className="w-6 h-6 text-teal" data-id="element-4117" />,
    title: 'Compliance',
    description: 'Essential regulatory training for all industries.'
  }, {
    icon: <HeartIcon className="w-6 h-6 text-teal" data-id="element-4118" />,
    title: 'Health & Safety',
    description: 'Workplace safety and risk management training.'
  }, {
    icon: <BookOpenIcon className="w-6 h-6 text-teal" data-id="element-4119" />,
    title: 'Food Safety',
    description: 'HACCP and food handling certifications.'
  }, {
    icon: <UserIcon className="w-6 h-6 text-teal" data-id="element-4120" />,
    title: 'HR & People',
    description: 'Employee management and workplace culture.'
  }, {
    icon: <UsersIcon className="w-6 h-6 text-teal" data-id="element-4121" />,
    title: 'Leadership',
    description: 'Team leadership and management skills.'
  }, {
    icon: <BriefcaseIcon className="w-6 h-6 text-teal" data-id="element-4122" />,
    title: 'Digital & Marketing',
    description: 'Digital skills and marketing strategies.'
  }, {
    icon: <DatabaseIcon className="w-6 h-6 text-teal" data-id="element-4123" />,
    title: 'Data & Security',
    description: 'Data protection and cybersecurity training.'
  }, {
    icon: <ClipboardCheckIcon className="w-6 h-6 text-teal" data-id="element-4124" />,
    title: 'Customer Service',
    description: 'Client relationship and service excellence.'
  }];
  return <section className="py-20 bg-white" data-id="element-4125">
      <div className="container mx-auto px-4 md:px-6" data-id="element-4126">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-4127">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-4128">
            Every skill. Every sector.
          </h2>
          <p className="text-lg text-charcoal/70" data-id="element-4129">
            From mandatory compliance to leadership, digital skills, and
            business growth.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-4130">
          {categories.map((category, index) => <div key={index} className="bg-gray-50 rounded-lg p-6 transition-all duration-300 hover:shadow-md" data-id="element-4131">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4 border border-gray-100" data-id="element-4132">
                {category.icon}
              </div>
              <h3 className="text-lg font-bold mb-2 text-charcoal" data-id="element-4133">
                {category.title}
              </h3>
              <p className="text-charcoal/70 text-sm mb-4" data-id="element-4134">
                {category.description}
              </p>
              <a href={`/training/${category.title.toLowerCase().replace(' & ', '-').replace(' ', '-')}`} className="text-teal font-medium hover:text-teal/80 transition-colors flex items-center text-sm" data-id="element-4135">
                Explore Courses
                <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-4136" />
              </a>
            </div>)}
        </div>
        <div className="text-center mt-12" data-id="element-4137">
          <a href="/training" className="px-8 py-4 bg-teal text-white font-medium rounded-lg transition-all duration-300 hover:bg-teal/90 inline-block shadow-lg" data-id="element-4138">
            View All Training Courses
          </a>
        </div>
      </div>
    </section>;
};
const ClosingCTASection = () => {
  return <section className="py-20 bg-gray-50" data-id="element-4139">
      <div className="container mx-auto px-4 md:px-6" data-id="element-4140">
        <div className="max-w-4xl mx-auto text-center" data-id="element-4141">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-4142">
            The Training Academy for Ireland, the UK & Europe.
          </h2>
          <p className="text-lg text-charcoal/70 mb-10 max-w-3xl mx-auto" data-id="element-4143">
            Individuals. Teams. Enterprises. One platform. Instant access.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center" data-id="element-4144">
            <a href="/training" className="px-8 py-4 bg-teal text-white font-medium rounded-lg transition-all duration-300 hover:bg-teal/90 text-center shadow-lg" data-id="element-4145">
              Start Training
            </a>
            <a href="/demo" className="px-8 py-4 border-2 border-charcoal text-charcoal font-medium rounded-lg hover:bg-charcoal hover:text-white transition-all duration-300 text-center" data-id="element-4146">
              Book a Demo
            </a>
          </div>
        </div>
      </div>
    </section>;
};
// Helper component for star icons
const StarIcon = ({
  className,
  fill
}) => {
  return <svg className={className} fill={fill} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" data-id="element-4147">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-4148" />
    </svg>;
};